import { Calendar, Clock } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-relay";
import { Label } from "src/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";

import type { ValidateCronExpressionMutation as ValidateCronExpressionMutationType } from "../mutations/__generated__/ValidateCronExpressionMutation.graphql";
import { ValidateCronExpressionMutation } from "../mutations/ValidateCronExpressionMutation";
import { formatCronExpression } from "../utils";

export type SchedulePresetType = "hourly" | "daily" | "weekdays" | "weekly" | "custom";

interface ScheduleBuilderProps {
  value: string;
  onChange: (value: string) => void;
  onError?: (error: boolean) => void;
}

const DAYS_OF_WEEK = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "0", label: "Sunday" },
];

// Standard cron text day names to numeric day-of-week (0=Sun, 1=Mon, ..., 6=Sat)
const DOW_NAME_TO_NUM: Record<string, string> = {
  sun: "0",
  mon: "1",
  tue: "2",
  wed: "3",
  thu: "4",
  fri: "5",
  sat: "6",
};

const MINUTE_OF_HOUR_OPTIONS = [
  { value: "0", label: ":00" },
  { value: "15", label: ":15" },
  { value: "30", label: ":30" },
  { value: "45", label: ":45" },
];

function generateTimeOptions(): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? "AM" : "PM";
      const label = `${hour12}:${m.toString().padStart(2, "0")} ${ampm}`;
      options.push({ value: `${h}:${m}`, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

/** Snap a minute value to the nearest 30-min interval used by the time picker */
function snapMinuteTo30(m: number): number {
  return m < 15 ? 0 : 30;
}

/** Snap a minute value to the nearest valid hourly option (0, 15, 30, 45) */
function snapMinuteToHourly(m: number): number {
  const valid = [0, 15, 30, 45];
  return valid.reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a));
}

interface ParsedScheduleType {
  preset: SchedulePresetType;
  hour: number;
  minute: number;
  dayOfWeek: string;
}

export function parseCronToSchedule(cron: string): ParsedScheduleType {
  if (!cron || !cron.trim()) {
    return { preset: "daily", hour: 9, minute: 0, dayOfWeek: "1" };
  }

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { preset: "custom", hour: 9, minute: 0, dayOfWeek: "1" };
  }

  const [minPart, hourPart, domPart, monthPart, dowPart] = parts;

  // Only `\d+` (minute) and `*`/`\d+` (hour) map to presets. Anything else —
  // commas, ranges, steps, names, `last`, etc. — must round-trip verbatim, so
  // route to custom before parseInt silently truncates "6,14,22" → 6.
  const minIsSimple = /^\d+$/.test(minPart);
  const hourIsSimple = /^(\*|\d+)$/.test(hourPart);
  if (!minIsSimple || !hourIsSimple) {
    return { preset: "custom", hour: 9, minute: 0, dayOfWeek: "1" };
  }

  const minute = parseInt(minPart, 10);
  const hour = parseInt(hourPart, 10);

  // Monthly/yearly crons can't be represented by the presets
  if (domPart !== "*" || monthPart !== "*") {
    return { preset: "custom", hour: isNaN(hour) ? 9 : hour, minute, dayOfWeek: "1" };
  }

  // Hourly: "N * * * *"
  if (hourPart === "*" && dowPart === "*") {
    const validHourlyMinutes = [0, 15, 30, 45];
    if (!validHourlyMinutes.includes(minute)) {
      return { preset: "custom", hour: 9, minute: 0, dayOfWeek: "1" };
    }
    return { preset: "hourly", hour: 9, minute, dayOfWeek: "1" };
  }

  if (isNaN(hour) || minute % 30 !== 0) {
    return { preset: "custom", hour: isNaN(hour) ? 9 : hour, minute, dayOfWeek: "1" };
  }

  if (dowPart === "*") {
    return { preset: "daily", hour, minute, dayOfWeek: "1" };
  }
  const dowLower = dowPart.toLowerCase();
  if (dowPart === "1-5" || dowLower === "mon-fri") {
    return { preset: "weekdays", hour, minute, dayOfWeek: "1" };
  }
  if (/^[0-6]$/.test(dowPart)) {
    return { preset: "weekly", hour, minute, dayOfWeek: dowPart };
  }
  const dowNum = DOW_NAME_TO_NUM[dowLower];
  if (dowNum !== undefined) {
    return { preset: "weekly", hour, minute, dayOfWeek: dowNum };
  }

  return { preset: "custom", hour, minute, dayOfWeek: "1" };
}

export function buildCron(preset: SchedulePresetType, hour: number, minute: number, dayOfWeek: string): string {
  switch (preset) {
    case "hourly":
      return `${minute} * * * *`;
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekdays":
      return `${minute} ${hour} * * 1-5`;
    case "weekly":
      return `${minute} ${hour} * * ${dayOfWeek}`;
    default:
      return "";
  }
}

const PRESETS: Array<{ value: SchedulePresetType; label: string }> = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom" },
];

export function ScheduleBuilder({ value, onChange, onError }: ScheduleBuilderProps) {
  const [initialState] = useState(() => {
    let cron = "0 9 * * *";
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      cron = parsed?.cron_expression || "0 9 * * *";
    } catch {
      // use default
    }
    return { cron, schedule: parseCronToSchedule(cron) };
  });

  const [preset, setPreset] = useState<SchedulePresetType>(initialState.schedule.preset);
  const [hour, setHour] = useState(initialState.schedule.hour);
  const [minute, setMinute] = useState(initialState.schedule.minute);
  const [dayOfWeek, setDayOfWeek] = useState(initialState.schedule.dayOfWeek);
  const [customCron, setCustomCron] = useState(initialState.schedule.preset === "custom" ? initialState.cron : "");
  const [customError, setCustomError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const [validateCronExpression] = useMutation<ValidateCronExpressionMutationType>(ValidateCronExpressionMutation);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const emitChange = useCallback(
    (cronExpression: string) => {
      onChange(JSON.stringify({ cron_expression: cronExpression, run_date: null }));
    },
    [onChange],
  );

  useEffect(() => {
    if (preset === "custom") return;
    const cron = buildCron(preset, hour, minute, dayOfWeek);
    emitChange(cron);
    onError?.(false);
  }, [preset, hour, minute, dayOfWeek, emitChange, onError]);

  const currentCron = preset === "custom" ? customCron : buildCron(preset, hour, minute, dayOfWeek);

  const handlePresetChange = (newPreset: SchedulePresetType) => {
    // When leaving custom, sync hour/minute/dayOfWeek from the custom cron input
    if (preset === "custom" && newPreset !== "custom" && customCron) {
      const parsed = parseCronToSchedule(customCron);
      let snappedMinute = parsed.minute;
      if (newPreset === "hourly" && ![0, 15, 30, 45].includes(parsed.minute)) {
        snappedMinute = snapMinuteToHourly(parsed.minute);
      } else if (newPreset !== "hourly" && parsed.minute % 30 !== 0) {
        snappedMinute = snapMinuteTo30(parsed.minute);
      }
      setHour(parsed.hour);
      setMinute(snappedMinute);
      setDayOfWeek(parsed.dayOfWeek);
      setPreset(newPreset);
      return;
    }

    // Snap minute to nearest 30-min interval when switching from hourly to a time-picker preset
    const needsTimeSnap = preset === "hourly" && newPreset !== "hourly" && newPreset !== "custom";
    if (needsTimeSnap && minute % 30 !== 0) {
      setMinute(snapMinuteTo30(minute));
    }
    setPreset(newPreset);
    if (newPreset === "custom") {
      const cron = buildCron(preset === "custom" ? "daily" : preset, hour, minute, dayOfWeek);
      setCustomCron(cron);
      emitChange(cron);
    }
  };

  const handleTimeChange = (timeValue: string) => {
    const [h, m] = timeValue.split(":").map(Number);
    setHour(h);
    setMinute(m);
  };

  const handleMinuteOfHourChange = (val: string) => {
    setMinute(parseInt(val, 10));
  };

  const handleCustomCronChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCron = e.target.value;
    setCustomCron(newCron);
    emitChange(newCron);

    if (!newCron.trim()) {
      setCustomError("Cron expression is required");
      onError?.(true);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setIsValidating(true);
      validateCronExpression({
        variables: { input: { cronExpression: newCron } },
        onCompleted: (response) => {
          if (response.validateCronExpression.isValid) {
            setCustomError(null);
            onError?.(false);
          } else {
            setCustomError(response.validateCronExpression.errorMessage || "Invalid cron expression");
            onError?.(true);
          }
          setIsValidating(false);
        },
        onError: () => {
          setCustomError("Error validating cron expression");
          onError?.(true);
          setIsValidating(false);
        },
      });
    }, 500);
  };

  const cronDescription = useMemo(() => {
    if (!currentCron?.trim()) return null;
    const formatted = formatCronExpression(currentCron);
    return formatted || null;
  }, [currentCron]);

  const showTimePicker = preset === "daily" || preset === "weekdays" || preset === "weekly";
  const showDayPicker = preset === "weekly";
  const showMinuteOfHour = preset === "hourly";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Schedule frequency">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => handlePresetChange(p.value)}
            aria-pressed={preset === p.value}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              preset === p.value
                ? "bg-primary text-primary-foreground"
                : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {showTimePicker && (
        <div className="flex gap-3 items-end">
          <div className="w-36">
            <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Time (UTC)
            </Label>
            <Select value={`${hour}:${minute}`} onValueChange={handleTimeChange}>
              <SelectTrigger className="bg-card border-border h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showDayPicker && (
            <div className="w-36">
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Day
              </Label>
              <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                <SelectTrigger className="bg-card border-border h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {showMinuteOfHour && (
        <div className="w-36">
          <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            At minute
          </Label>
          <Select value={String(minute)} onValueChange={handleMinuteOfHourChange}>
            <SelectTrigger className="bg-card border-border h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MINUTE_OF_HOUR_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {preset === "custom" && (
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Cron Expression</Label>
          <input
            type="text"
            value={customCron}
            onChange={handleCustomCronChange}
            placeholder="e.g., 0 9 * * 1"
            className={`w-full px-3 py-1.5 rounded-md font-mono text-sm bg-card border text-foreground ${
              customError ? "border-red-500" : "border-border"
            }`}
          />
          {isValidating && (
            <div className="text-muted-foreground text-xs mt-1 flex items-center">
              <div className="animate-spin h-3 w-3 mr-2 border border-current rounded-full border-t-transparent" />
              Validating...
            </div>
          )}
          {!isValidating && customError && <div className="text-red-500 text-xs mt-1" role="alert">{customError}</div>}
        </div>
      )}

      {cronDescription && !customError && (
        <div className="text-xs text-green-400">{cronDescription}</div>
      )}
    </div>
  );
}

export default ScheduleBuilder;
